/**
 * Generic type helper function because TS doesn't have type inference for generic types
 * This enforces that the `light` and `dark` properties have the same keys
 */
const ComponentThemeMapping = (value) => ({ ...value }); // The `as const` is here and not on the input object outside because it gives more subtle/more readable error messages
/**
 * Generic helper function to generate the final theme objects
 */
const GenerateThemeObject = (value) => Object.fromEntries(Object.entries(value).map((entry) => [kebabize(entry[0]), entry[1]]));
const kebabize = (str) => str.replace(/[A-Z]+(?![a-z])|[A-Z]/g, (letter, ofs) => (ofs ? "-" : "") + letter.toLowerCase());
const colors = {
    blueprint700: "var(--blueprint-700)",
    blueprint600: "var(--blueprint-600)",
    blueprint500: "var(--blueprint-500)",
    blueprint400: "var(--blueprint-400)",
    blueprint300: "var(--blueprint-300)",
    blueprint200: "var(--blueprint-200)",
    blueprint100: "var(--blueprint-100)",
    error: "var(--error)",
    red: "var(--red)",
    green: "var(--green)",
    yellow: "var(--yellow)",
    black: "var(--black)",
    gray900: "var(--gray-900)",
    gray800: "var(--gray-800)",
    gray500: "var(--gray-500)",
    gray400: "var(--gray-400)",
    gray300: "var(--gray-300)",
    gray100: "var(--gray-100)",
    gray50: "var(--gray-50)",
    gray20: "var(--gray-20)",
    white: "var(--white)",
    black10percent: "var(--black-10percent)",
    black5percent: "var(--black-5percent)",
    black2percent: "var(--black-2percent)",
    white64percent: "var(--white-64percent)",
    white32percent: "var(--white-32percent)",
    white20percent: "var(--white-20percent)",
    white10percent: "var(--white-10percent)",
};
const legacyGeneralStyles = ComponentThemeMapping({
    light: {
        primary: colors.blueprint500,
        primaryHover: colors.blueprint600,
        primaryActive: colors.blueprint400,
        background: colors.white,
        textMain: colors.black,
        textDimmed: colors.gray800,
        textInput: colors.gray800,
        textLightInput: colors.gray800,
        textLightInputHover: colors.gray800,
        textLightInputHoverBorder: colors.gray300,
        textLightInputRestBorder: colors.gray300,
        textLightInputActiveBackground: colors.gray400,
        textLightInputActiveBorder: colors.gray300,
        elementsShadow: colors.gray50,
        elementsHighlight: colors.gray300,
        elementsBorder: colors.gray100,
        elementsBackground: colors.gray100,
        controlError: colors.error,
        controlLabelColor: colors.gray800,
        controlLabelSecondaryColor: colors.gray500,
        fieldBackground: colors.white,
        fieldHoverBackground: colors.black2percent,
        fieldBorder: colors.black10percent,
        selectInvalid: "rgba(0, 0, 0, 0.4)",
    },
    dark: {
        primary: colors.blueprint300,
        primaryHover: colors.blueprint400,
        primaryActive: colors.blueprint200,
        background: colors.gray900,
        textMain: colors.white,
        textDimmed: colors.gray400,
        textInput: colors.gray50,
        textLightInput: colors.gray300,
        textLightInputHover: colors.gray100,
        textLightInputHoverBorder: colors.gray800,
        textLightInputRestBorder: colors.gray500,
        textLightInputActiveBackground: colors.gray500,
        textLightInputActiveBorder: colors.gray400,
        elementsShadow: colors.gray800,
        elementsBorder: colors.gray500,
        elementsHighlight: colors.white,
        elementsBackground: colors.gray800,
        controlError: colors.error,
        controlLabelColor: colors.white,
        controlLabelSecondaryColor: colors.gray400,
        fieldBackground: "rgba(0, 0, 0, 0.2)",
        fieldHoverBackground: colors.black10percent,
        fieldBorder: colors.white20percent,
        selectInvalid: "rgba(255, 255, 255, 0.4)",
    },
});
const textStyles = ComponentThemeMapping({
    light: {
        textHeaderLabel: colors.black,
        textPrimaryLabel: colors.gray800,
        textSecondaryLabel: colors.gray500,
        textTertiaryLabel: colors.gray400,
    },
    dark: {
        textHeaderLabel: colors.white,
        textPrimaryLabel: colors.white,
        textSecondaryLabel: colors.gray400,
        textTertiaryLabel: colors.gray300,
    },
});
const controlStyles = ComponentThemeMapping({
    light: {
        controlLabel: colors.gray800,
        controlSecondaryLabel: colors.gray500,
        controlLabelOnAccent: colors.white,
        controlAccentLabel: colors.blueprint500,
        controlAccentLabelHover: colors.blueprint600,
        controlBackground: colors.white64percent,
        controlBackgroundHover: colors.black2percent,
        controlBackgroundActive: colors.black5percent,
        controlAccentBackground: colors.blueprint500,
        controlAccentBackgroundHover: colors.blueprint600,
        controlAccentBackgroundActive: colors.blueprint400,
        controlFocusHighlight: "rgba(29, 78, 255, 0.4)",
        controlErrorLabel: colors.error,
        controlWarningLabel: colors.yellow,
        controlSuccessLabel: colors.green,
    },
    dark: {
        controlLabel: colors.white,
        controlSecondaryLabel: colors.gray400,
        controlLabelOnAccent: colors.white,
        controlAccentLabel: colors.blueprint400,
        controlAccentLabelHover: colors.blueprint500,
        controlBackground: colors.white10percent,
        controlBackgroundHover: colors.white20percent,
        controlBackgroundActive: colors.white32percent,
        controlAccentBackground: colors.blueprint400,
        controlAccentBackgroundHover: colors.blueprint500,
        controlAccentBackgroundActive: colors.blueprint300,
        controlFocusHighlight: "rgb(83, 120, 255, 0.4)",
        controlErrorLabel: colors.error,
        controlWarningLabel: colors.yellow,
        controlSuccessLabel: colors.green,
    },
});
const infobarStyles = ComponentThemeMapping({
    light: {
        snackbarBackground: "#e5e5e5",
    },
    dark: {
        snackbarBackground: "#e5e5e5",
    },
});
const backgroundStyles = ComponentThemeMapping({
    light: {
        dialogOverlay: colors.black,
        dialogBackground: colors.white,
        pricingpageBackground: colors.gray20,
    },
    dark: {
        dialogOverlay: colors.white,
        dialogBackground: colors.gray900,
        pricingpageBackground: colors.gray900,
    },
});
const lightTheme = GenerateThemeObject({
    ...legacyGeneralStyles.light,
    ...textStyles.light,
    ...controlStyles.light,
    ...infobarStyles.light,
    ...backgroundStyles.light,
});
const darkTheme = GenerateThemeObject({
    ...legacyGeneralStyles.dark,
    ...textStyles.dark,
    ...controlStyles.dark,
    ...infobarStyles.dark,
    ...backgroundStyles.dark,
});

export { darkTheme, lightTheme };
//# sourceMappingURL=themes.js.map
